/* eslint-disable @typescript-eslint/naming-convention */
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'

export type PackageStructureItemType = {
  id: string
  to_unit: {
    id: string
    description: string
    group: string
    active: boolean
  }
  product_id: number
  multiplier: number
  barcode: string
  purchase_price: number
  purchase_currency: string
  is_selling_unit: boolean
  is_selling_unit_secondary: boolean
  multiplier_range_start: number
  multiplier_range_end: number
  width: number
  height: number
  length: number
  dimension_unit: {
    id: string
    description: string
    group: string
    active: boolean
  }
  volume: number
  volume_unit: {
    id: string
    description: string
    group: string
    active: boolean
  }
  net_weight: number
  gross_weight: number
  weight_unit: {
    id: string
    description: string
    group: string
    active: boolean
  }
  created_by: string
  updated_by: string
  created_at: number
  updated_at: number
  type: string
}

export type PackageStructureWimsItemType = {
  volume: number
  net_weight: number
  range_qty_max: number
  product_id: number
  purchase_currency: string
  length: number
  gross_weight: number
  to_uom_id: string
  use_range_qty: boolean
  range_qty_min: number
  barcode: string
  created_at: string
  updated_at: string
  from_uom_description: string
  to_uom_description: string
  type: string
  id: number
  qty: number
  purchase_price: number
  barcode_source_level: 'BASE' | 'INNER' | 'SELF'
  from_uom_id: string
  width: number
  height: number
  selling_type?: '' | 'PRIMARY' | 'SECONDARY'
}

type PaginationDataType = {
  direction: string
  number_of_elements: number
  page_index: number
  page_size: number
  sort: string
}

export type PackageStructureResponseType = {
  pagination: PaginationDataType
  data: PackageStructureItemType[]
}

export type PackageStructureWimsResponseType = {
  pagination: PaginationDataType
  data: PackageStructureWimsItemType[]
}

export type SavePackageStructureRequestType = {
  id?: string
  to_unit_id: string
  product_id: number
  multiplier: number
  barcode: string
  purchase_price: number
  purchase_currency: string
  is_selling_unit: boolean
  is_selling_unit_secondary: boolean
  multiplier_range_start?: number
  multiplier_range_end?: number
  width: number
  height: number
  length: number
  dimension_unit: string
  volume: number
  volume_unit: string
  net_weight: number
  gross_weight: number
  weight_unit: string
}

type GetWimsPackageStructureParamsType = {
  product_id?: number
  is_deleted?: boolean
}

type WimsPackageStructureItemType = {
  id: string
  product_id: number
  to_unit_id: string
  multiplier: number
  barcode: string
  purchase_price: number
  purchase_currency: string
  is_selling_unit: boolean
}

type GetPackageStructureParamsType = {
  'pagination.page_size': number
  'pagination.page_index': number
}

export type CreateUpdatePackageStructurePayloadType = {
  product_id: number
  type: 'BASE' | 'INNER' | 'OUTER' | 'CUSTOM'
  from_uom_id: string
  to_uom_id: string
  use_range_qty: boolean
  qty?: number
  range_qty_min?: number
  range_qty_max?: number
  barcode: string
  barcode_source_level: 'BASE' | 'INNER' | 'SELF'
  purchase_price: number
  purchase_currency: string
  width: number
  height: number
  length: number
  volume: number
  net_weight: number
  gross_weight: number
}

export type PostPutDeleteResponseType = {
  message: string
}

export type PutPackageStructureSellingTypePayloadType = {
  selling_type: PackageStructureWimsItemType['selling_type']
}

type GetProductDetailByIdResponseType = {
  product_name: string
  selling_unit_grammation_description?: string
  product_sku_no: string
  product_image_urls: {
    sequence: number
    url_large: string
    url_medium: string
    url_small: string
  }[]
  product_categories: {
    id: number
    name: string
    is_primary: boolean
  }[]
  base_uom: {
    group: string
    description: string
    active: boolean
    id: string
  }
  product_id: number
}

export const getPackageStructure = (productId: number, params: GetPackageStructureParamsType) =>
  axiosInstanceApiGateway.Get<PackageStructureResponseType, true>({
    url: `/ims/internal/v1/products/${productId}/package-structures`,
    params,
  })

export const deletePackageStructure = (id: number) =>
  axiosInstanceApiGateway.Delete({ url: `/ims/internal/v1/products/package-structures/${id}` })

export const savePackageStructure = (data: SavePackageStructureRequestType) =>
  axiosInstanceApiGateway.Put<{ message: string }, true>({
    url: `/ims/internal/v1/products/package-structures`,
    data,
  })

export const getPackageStructureWims = (productId: number, params: GetPackageStructureParamsType) =>
  axiosInstanceApiGateway.Get<PackageStructureWimsResponseType, true>({
    url: `/wims/internal/v1/products/${productId}/packaging-structures`,
    params,
  })

export const getPackageStructureDetail = (id: number) =>
  axiosInstanceApiGateway.Get<PackageStructureWimsItemType>({
    url: `/wims/internal/v1/products/packaging-structures/${id}`,
  })

export const deletePackageStructureWims = (id: number) =>
  axiosInstanceApiGateway.Delete<PostPutDeleteResponseType, true>({
    url: `/wims/internal/v1/products/packaging-structures/${id}`,
  })

export const postPackageStructureWims = (data: CreateUpdatePackageStructurePayloadType) =>
  axiosInstanceApiGateway.Post<PostPutDeleteResponseType, true>({
    url: `/wims/internal/v1/products/packaging-structures`,
    data,
  })

export const putPackageStructureWims = (
  id: number,
  data: CreateUpdatePackageStructurePayloadType,
) =>
  axiosInstanceApiGateway.Put<PostPutDeleteResponseType, true>({
    url: `/wims/internal/v1/products/packaging-structures/${id}`,
    data,
  })

export const putPackageStructureSellingTypeById = (
  id: number,
  data: PutPackageStructureSellingTypePayloadType,
) =>
  axiosInstanceApiGateway.Put<PostPutDeleteResponseType, true>({
    url: `/wims/internal/v1/products/packaging-structures/${id}/selling-type`,
    data,
  })

export const getProductDetailById = (id: number) =>
  axiosInstanceApiGateway.Get<GetProductDetailByIdResponseType>({
    url: `/wims/internal/v1/products/${id}`,
  })

type GetUomParamsType = {
  'filter.id'?: number
  'filter.active'?: boolean
  'filter.groups': string
  'filter.name'?: string
  'pagination.page_size': number
  'pagination.page_index': number
}

export const getUom = (params: GetUomParamsType) =>
  axiosInstanceApiGateway.Get({ url: '/wims/internal/v1/products/uoms', params })
